import { useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  type ShouldRevalidateFunction
} from 'react-router';
import { enableStaticRendering } from 'mobx-react-lite';
// The file, not the vendors barrel: the barrel also loads Firebase, Forter and
// reCAPTCHA, which this module would put on every page.
import { GtmNoScript, GtmScript } from '@link/share/vendors/gtmSnippet';
import type { Route } from './+types/root';
import { createAppI18n, resolveAppLocale } from './i18n';
import { getTranslations } from './i18n/translations.server';
import '@link/share/styles/index.css';
import '@link/triplabo/styles/root.css';

// `observer` components render on the server too (the product page's plans).
// A server render is a single pass, so nothing there should subscribe to the
// stores: without this, each render leaves reactions behind to be swept later.
enableStaticRendering(import.meta.env.SSR);

/**
 * Document-level `<link>` tags.
 *
 * The SPA declared the favicon in `index.html` as `/src/assets/triplabo-logo.svg`
 * — a source path that only resolved because Vite rewrote it at build time, and
 * framework mode has no `index.html` to carry it over.
 *
 * Served from `public/` rather than imported: at 1.1KB the file is under Vite's
 * 4KB `assetsInlineLimit`, so importing it emits a data URI that is re-sent
 * inside every SSR response instead of being fetched once and cached.
 */
export const links: Route.LinksFunction = () => [
  { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' }
];

/**
 * The page's language and its translations.
 *
 * Only the one language the page renders in reaches the browser, as part of
 * the loader data React Router already serializes into the HTML; the client
 * bundle carries no translations at all. Route `meta` functions read it too
 * (`matches[0]`), since they also run in the browser on client navigations.
 */
export function loader({ params }: Route.LoaderArgs) {
  const locale = resolveAppLocale(params.lang);
  return { locale, translations: getTranslations(locale) };
}

/**
 * Refetch the translations only when the language changes. Every other
 * navigation stays in the same language and keeps the ones it has.
 */
export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentParams,
  nextParams
}) => currentParams.lang !== nextParams.lang;

/**
 * The locale for the current render, read off the first path segment.
 *
 * Deliberately not `useParams().lang`: `Layout` also wraps the root
 * `ErrorBoundary`, and on an unmatched URL there is no matched route and
 * therefore no `lang` param. Every `/ja/*` 404 then rendered `<html lang="en">`,
 * losing the visitor's language on the error page — and when the 404 was reached
 * by client-side navigation from `/ja`, the document already carried
 * `lang="ja"`, so React reported a hydration mismatch on `<html>` and stopped
 * patching it up.
 *
 * The pathname is available on every render, matched or not, so both sides agree
 * and the error page stays in the visitor's language. A first segment that is
 * not a supported locale still falls back to the default.
 */
function useLocale() {
  const { pathname } = useLocation();
  return resolveAppLocale(pathname.split('/')[1]);
}

// Build-time constants, so GTM is gated the same way as in the SPA's
// `src/vendors/gtm.ts` rather than loading in local development.
const GTM_ID = 'GTM-PQNQVDRQ';
const APP_ENV = import.meta.env.VITE_APP_TRIPLABO_ENV ?? '';

export function Layout({ children }: { children: React.ReactNode }) {
  const locale = useLocale();
  return (
    <html lang={locale} translate="no">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <Meta />
        <Links />
        {/* In the document rather than injected after hydration, so the tag
            fires on first paint — see share/vendors/gtmSnippet. */}
        <GtmScript gtmId={GTM_ID} environment={APP_ENV} />
      </head>
      <body>
        {/* Must be the first element in <body> per GTM's install instructions. */}
        <GtmNoScript gtmId={GTM_ID} environment={APP_ENV} />
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  const { locale, translations } = loaderData;

  // One i18next instance per language, created during render rather than
  // shared at module scope, so each server request gets its own. Switching
  // language is an ordinary navigation: the root loader returns the new
  // language's translations alongside the page's data, and the new instance
  // renders the new page in one step.
  const i18n = useMemo(
    () => createAppI18n(locale, translations),
    [locale, translations]
  );

  return (
    <I18nextProvider i18n={i18n}>
      <Outlet />
    </I18nextProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404
        ? 'The requested page could not be found.'
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main>
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre>
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
